import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, CircularProgress, ThemeProvider } from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import usePostAuthNavigation from '../hooks/usePostAuthNavigation';

import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { logError } from '@/utils/logError';
import { buildRedirectParam } from '@/utils/urlHelpers';
import {
  REDIRECT_TO_SESSION_KEY,
  CANOPY_SIGN_UP_CALLBACK,
  CANOPY_LOGIN_CALLBACK,
} from '@/utils/constants';
import { SLEEPIO_CANOPY_PROGRAM } from '@/utils/posthog';
import { shouldAskAboutPassword } from '@/utils/passwordAccount';
import { setPreferredSignInMethod, trackSignInSucceeded } from '@/utils/signInAnalytics';
import sleepioTheme from '@/themes/sleepioTheme';

// Entry point for external services that still redirect to the old
// /activate/sleepio-canopy/:lob/:org_slug/:intent/email/callback format.
// Waits 1500ms for Supabase to settle the session before calling getSession(),
// then routes to /coverage (Create) or /complete (Login) via usePostAuthNavigation.

const EmailCallbackInner: React.FC = () => {
  const history = useHistory();
  const { state, dispatch } = useActivation();
  const base = useActivationBase();
  const fallbackCallback = state.accountIntent === AccountIntent.Signin
    ? CANOPY_LOGIN_CALLBACK
    : CANOPY_SIGN_UP_CALLBACK;
  const effectiveRedirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY) || fallbackCallback;
  const navigatePostAuth = usePostAuthNavigation(effectiveRedirectTo);
  const posthog = usePostHog();

  useEffect(() => {
    const navigateToAuth = () => history.replace(`${base}/auth`);

    const handleCallback = async () => {
      await new Promise((resolve) => { setTimeout(resolve, 1500); });

      const { data: { session } } = await getSupabaseClient().auth.getSession();
      if (!session) {
        logError('[EmailCallback]', 'No session after magic link');
        posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
        navigateToAuth();
        return;
      }

      dispatch({ type: 'SET_SESSION', session });

      // The link produced a session, so this is a completed sign-in like any other.
      if (state.accountIntent === AccountIntent.Signin) {
        trackSignInSucceeded(posthog);
        setPreferredSignInMethod(posthog, 'magic_link');
      }

      if (state.accountIntent === AccountIntent.Create) {
        await activateSleepioConfirm({ accessToken: session.access_token });
      }

      // The emailed link has just confirmed this address, which is the one moment
      // worth asking whether they would like a password — they are signed in, so
      // saying yes costs no second email. Only asked of someone who has no password,
      // and only once: declining is remembered for the session.
      if (shouldAskAboutPassword(session.user)) {
        // Carry the app's deep link onto the URL. This detour skips the navigation
        // helper that was until now the only thing passing it along, and a member on a
        // phone who loses it here finishes every step and is still stranded.
        history.replace(`${base}/password/prompt${buildRedirectParam(effectiveRedirectTo)}`);
        return;
      }

      navigatePostAuth({ replace: true });
    };

    handleCallback().catch((err) => {
      logError('[EmailCallback]', 'Callback failed', err);
      posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
      navigateToAuth();
    });
  }, []); // eslint-disable-line

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
};

export const EmailCallback: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailCallbackInner />
  </ThemeProvider>
);
